import { ResizeController } from "@lit-labs/observers/resize-controller.js";
import { html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";

import { getSlideData as getSlidePresentationData } from "../../data/classroom";
import { toSec } from "../../utils/timecode";
import { DEFAULT_ZOOM_FILL_SCALE } from "../zoomable-slide/zoomable-slide";

import styles from "./slide-presentation.css";

import type {
  FlattenedPresentationSlideData,
  PresentationData,
  PresentationSlideData,
  SlideAnimation,
  SlideAnimationVariant,
  SlideData,
  SlideHighlightState,
  SlideLookupState,
  SlideSpotlightState,
  SlideState,
  SlideTransformState,
} from "../../types.d";
import type { SlideViewMode } from "../slide/slide";
import type { PropertyValueMap, PropertyValues } from "lit";
import type { Ref } from "lit/directives/ref.js";

export type PresentationDecoration = "border" | "box-shadow" | "none";
export interface PresentationTheme {
  artwork?: {
    class?: string;
    color?: PresentationThemeColor;
    module?: string;
  };
}

export interface PresentationThemeColor {
  primary: string;
  secondary: string;
}

export type PresentationViewMode = "default" | "tall";

const DEFAULT_ASPECT_RATIO = 16 / 9;
const TALL_MODE_THRESHOLD = 16 / 10;
const MOBILE_MODE_THRESHOLD = 0.6;
const HIGHLIGHT_VARIANTS: Set<SlideAnimationVariant> = new Set([
  "hide-highlight",
  "scroll-and-highlight",
  "show-highlight",
]);

function parseDataJSON(dataJSON: null | string): PresentationData {
  if (!dataJSON) return { presentationSlides: [] };
  const parsedData = JSON.parse(dataJSON);

  if (parsedData["presentationSlides"]) {
    return parsedData as PresentationData;
  } else if (parsedData.length) {
    const presentationSlides: Array<PresentationSlideData> = parsedData.map(
      (flattenedSlide: FlattenedPresentationSlideData) => {
        return JSON.parse(flattenedSlide.config) as PresentationSlideData;
      },
    );

    return { presentationSlides };
  } else {
    return { presentationSlides: [] };
  }
}

@customElement("bp-slide-presentation")
export class Presentation extends LitElement {
  static styles = styles;

  @property({ attribute: "arc-id" })
  arcId?: string;

  @state()
  containerAspectRatio = 16 / 9;

  @state()
  containerWidth = 960;

  @property({ type: Number, attribute: "current-time" })
  currTime = 0;

  @property({
    type: Object,
    attribute: "data",
    converter: parseDataJSON,
  })
  data?: PresentationData;

  @property({ attribute: "decoration" })
  decoration: PresentationDecoration = "none";

  @property({
    type: Boolean,
    attribute: "no-video",
    converter: (value) => (value === "true" ? true : false),
  })
  noVideo?: boolean = false;

  @property({
    type: Boolean,
    attribute: "paused",
    converter: (value) => (value === "true" ? true : false),
  })
  paused?: boolean = true;

  @property({ type: Number, attribute: "playback-rate" })
  playbackRate = 1;

  presentationRef: Ref<HTMLElement> = createRef();

  @state()
  slideViewMode: SlideViewMode = "default";

  @property({ type: Object })
  theme?: PresentationTheme;
  @property({ attribute: "view-mode" })
  viewMode?: PresentationViewMode = "default";

  get currAnimations(): Array<SlideAnimation> | null {
    return (
      this.data?.presentationSlides[this.currSlideIndex].animations ?? null
    );
  }

  get currHighlightState(): null | SlideHighlightState {
    const highlightHidden: Set<string> = new Set();
    const highlightShown: Set<string> = new Set();

    // Start with every highlight deactivated.
    if (this.currAnimations) {
      for (const animation of this.currAnimations) {
        if (HIGHLIGHT_VARIANTS.has(animation.variant))
          highlightHidden.add(animation.stringValue ?? "");
      }
    }

    // It's possible for the index to be null, if the first animation is
    // after current time. We still want to return a valid state in this case.
    this.iterateToCurrAnimation(
      ({ variant, stringValue, stringArrayValue }) => {
        const stringArray = stringArrayValue ?? [stringValue];
        switch (variant) {
          case "hide-highlight":
            stringArray.forEach((stringValue) => {
              if (!stringValue) return;
              highlightShown.delete(stringValue);
              highlightHidden.add(stringValue);
            });
            break;
          case "scroll-and-highlight":
          case "show-highlight":
            stringArray.forEach((stringValue) => {
              if (!stringValue) return;
              highlightShown.add(stringValue);
              highlightHidden.delete(stringValue);
            });
            break;
          default:
            // Do nothing
            break;
        }
      },
    );

    return {
      shown: Array.from(highlightShown.keys()),
      hidden: Array.from(highlightHidden.keys()),
    };
  }

  get currLookupState(): SlideLookupState {
    let shown = undefined;
    this.iterateToCurrAnimation(({ variant, stringValue }) => {
      switch (variant) {
        case "clear-lookup":
          shown = undefined;
          break;
        case "lookup":
          shown = stringValue;
          break;
        default:
          // Do nothing
          break;
      }
    });

    return { shown };
  }

  get currSlide(): null | SlideData {
    return this.data?.presentationSlides[this.currSlideIndex].slide ?? null;
  }

  @state()
  get currSlideIndex(): number {
    if (!this.data) return 0;
    let newSlide = 0;
    while (newSlide < this.data.presentationSlides.length - 1) {
      if (
        toSec(this.data.presentationSlides[newSlide + 1].startTime) >
        this.currTime
      )
        break;
      newSlide++;
    }

    return newSlide;
  }

  get currSlideState(): null | SlideState {
    if (!this.currAnimations?.length) return null;

    return {
      highlightState: this.currHighlightState ?? undefined,
      transformState: this.currTransformState,
      spotlightState: this.currSpotlightState,
      lookupState: this.currLookupState,
    };
  }

  get currSpotlightState(): SlideSpotlightState {
    let spotlighted: Array<string> = [];
    this.iterateToCurrAnimation(
      ({ variant, stringValue, stringArrayValue }) => {
        const stringArray: Array<string> = stringArrayValue ?? [
          stringValue ?? "",
        ];
        switch (variant) {
          case "add-spotlight":
            spotlighted = spotlighted.concat(stringArray);
            break;
          case "clear-spotlight":
            spotlighted = [];
            break;
          case "scroll-and-spotlight":
          case "spotlight":
            spotlighted = stringArray;
            break;
          default:
            // Do nothing
            break;
        }
      },
    );

    return { spotlighted };
  }

  @state()
  get currStateIndex(): null | number {
    if (
      !this.currAnimations?.length ||
      this.currTime < toSec(this.currAnimations[0].startTime)
    ) {
      return null;
    }
    let index = 0;
    while (
      index < this.currAnimations.length - 1 &&
      this.currTime >= toSec(this.currAnimations[index + 1].startTime)
    ) {
      index++;
    }

    return index;
  }

  get currTransformState(): SlideTransformState | undefined {
    if (!this.currAnimations) return undefined;
    let minimizeTitle = false,
      scrollAnchorPoint: string | undefined = undefined,
      scrollPoints: Array<string> | undefined = undefined,
      scrollX: number | undefined = 0,
      scrollY: number | undefined = 0,
      zoom: number | undefined = 0,
      zoomFillElements: Array<string> | undefined = undefined,
      zoomFitElements: Array<string> | undefined = undefined;
    this.iterateToCurrAnimation(
      ({ variant, numberValue, stringValue, stringArrayValue, extraArg }) => {
        const stringArray =
          stringArrayValue ?? (stringValue ? [stringValue] : undefined);
        switch (variant) {
          case "expand-title":
            minimizeTitle = false;
            break;
          case "minimize-title":
            minimizeTitle = true;
            break;
          case "scroll-and-highlight":
          case "scroll-and-spotlight":
          case "scroll-point":
            scrollPoints = stringArray;
            scrollAnchorPoint = extraArg;
            scrollY = undefined;
            scrollX = undefined;
            break;
          case "scroll-x":
            scrollX = numberValue;
            scrollY = scrollY || 0;
            scrollPoints = undefined;
            scrollAnchorPoint = undefined;
            break;
          case "scroll-y":
            scrollX = scrollX || 0;
            scrollY = numberValue;
            scrollPoints = undefined;
            scrollAnchorPoint = undefined;
            break;
          case "zoom":
            zoom = numberValue;
            zoomFillElements = undefined;
            zoomFitElements = undefined;
            break;
          case "zoom-to-cover":
            zoom = DEFAULT_ZOOM_FILL_SCALE;
            zoomFillElements = undefined;
            zoomFitElements = undefined;
            scrollY = 0.5;
            break;
          case "zoom-to-fill":
            zoom = stringArray ? undefined : 1; // If no argument, default to zoom 1
            zoomFillElements = stringArray;
            zoomFitElements = undefined;
            break;
          case "zoom-to-fit":
            zoom = stringArray ? undefined : 0; // If no argument, default to zoom 1
            zoomFillElements = undefined;
            zoomFitElements = stringArray;
            break;
          default:
            // Do nothing
            break;
        }
      },
    );

    return {
      minimizeTitle,
      zoom,
      zoomFillElements,
      zoomFitElements,
      scrollX,
      scrollY,
      scrollPoints,
      scrollAnchorPoint,
    };
  }

  get presentationBackgroundColor() {
    switch (this.currSlide?.variant ?? "title") {
      case "main-point":
        return this.theme?.artwork?.color?.primary ?? "#fff";
      case "word-definition":
        return this.theme?.artwork?.color?.secondary ?? "#fff";
      default:
        return "#fff";
    }
  }

  get presentationMargin() {
    return (
      (Math.max(
        0,
        (this.containerAspectRatio - DEFAULT_ASPECT_RATIO) /
          this.containerAspectRatio,
      ) /
        2) *
      this.containerWidth
    );
  }

  @state()
  get presentationScale() {
    return this.containerWidth > 0
      ? this.containerAspectRatio <= DEFAULT_ASPECT_RATIO
        ? this.containerWidth / 960
        : this.containerWidth / this.containerAspectRatio / 540
      : 1;
  }

  get themeStyles() {
    const props = [];

    if (this.theme?.artwork?.color) {
      props.push(
        `--theme-artwork-primary-color: ${this.theme?.artwork?.color.primary};`,
      );
      props.push(
        `--theme-artwork-secondary-color: ${this.theme?.artwork?.color.secondary};`,
      );
    }

    return props.join(" ");
  }

  private prevAspect: null | number = null;

  private prevScale: null | number = null;

  private prevSlideIndex: null | number = null;

  private prevStateIndex: null | number = null;

  private resizeController = new ResizeController(this, {
    callback: (entries) => {
      if (entries.length > 0) {
        entries
          .filter((entry) =>
            entry.target.className.includes("slide-presentation"),
          )
          .forEach((entry) => {
            this.containerWidth = entry.contentRect.width;
            this.containerAspectRatio = entry.contentRect.height
              ? entry.contentRect.width / entry.contentRect.height
              : 16 / 9;
          });
      }
    },
  });

  firstUpdated() {
    if (this.presentationRef?.value) {
      this.resizeController.observe(this.presentationRef.value);
    }
  }

  render() {
    const style = `
      ${this.themeStyles}
      --tall-mode-unscaled-height: ${100 / this.presentationScale}%;
      --presentation-margin: ${this.presentationMargin}px;
      --presentation-background-color: ${this.presentationBackgroundColor};
      --presentation-scale: ${this.presentationScale.toString()};
      --aspect: ${this.containerAspectRatio};
    `;

    return html`
      <div
        ${ref(this.presentationRef)}
        class="slide-presentation ${this.viewMode} decoration-${this
          .decoration}"
        style="${style}"
      >
        <div class="scaled-slides ${this.viewMode}">
          ${this.data
            ? html`
                <bp-slide
                  primary-color="${this.theme?.artwork?.color?.primary ??
                  "#104366"}"
                  secondary-color="${this.theme?.artwork?.color?.secondary ??
                  "#e24213"}"
                  view-mode="${this.slideViewMode}"
                  current-time="${this.currTime}"
                  paused="${this.paused}"
                  no-video="${this.noVideo}"
                  playback-rate="${this.playbackRate}"
                  .data=${this.currSlide}
                  .slideState=${this.currSlideState}
                  .theme=${this.theme}
                />
              `
            : ""}
        </div>
      </div>
    `;
  }

  updated(changedProperties: PropertyValues) {
    this.prevSlideIndex = this.currSlideIndex;
    this.prevStateIndex = this.currStateIndex;
    this.prevScale = this.presentationScale;
    this.prevAspect = this.containerAspectRatio;

    if (!changedProperties.has("arcId") || !this.arcId) return;

    getSlidePresentationData(this.arcId)
      .then(({ data }) => {
        this.data = parseDataJSON(JSON.stringify(data));
      })
      .catch(() => undefined);
  }

  willUpdate() {
    this.slideViewMode =
      this.containerAspectRatio > TALL_MODE_THRESHOLD
        ? this.presentationScale > MOBILE_MODE_THRESHOLD
          ? "default"
          : "mobile"
        : this.presentationScale > MOBILE_MODE_THRESHOLD
          ? "tall"
          : "mobile-tall";
  }

  protected shouldUpdate(
    changedProperties: Map<PropertyKey, unknown> | PropertyValueMap<this>,
  ): boolean {
    if (
      changedProperties.has("data") ||
      changedProperties.has("arcId") ||
      changedProperties.has("viewMode") ||
      changedProperties.has("decoration")
    ) {
      return true;
    }

    if (
      this.data &&
      (this.currSlide?.variant === "illustration" ||
        this.currSlide?.variant === "screen-recording") &&
      (changedProperties.has("currTime") ||
        changedProperties.has("paused") ||
        changedProperties.has("playbackRate"))
    ) {
      return true;
    }

    if (
      this.prevSlideIndex != this.currSlideIndex ||
      this.prevStateIndex != this.currStateIndex ||
      this.prevScale != this.presentationScale ||
      this.prevAspect != this.containerAspectRatio
    ) {
      return true;
    }

    return false;
  }

  private iterateToCurrAnimation(fn: (animation: SlideAnimation) => void) {
    if (!this.currAnimations?.length || this.currStateIndex == null) return;
    for (let index = 0; index <= this.currStateIndex; index++) {
      fn(this.currAnimations[index]);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bp-slide-presentation": Presentation;
  }
}
